import { DropdownOptionType, IDropdownMenuOption } from '@evinced-private/ui-common';

import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { DASHBOARD_LABELS } from 'src/common/labels/dashboard.labels';

type getEvincedAppsMenuOptionsHelperArgs = {
	hasMfaLicense: boolean;
	hasMsdkLicense: boolean;
	hasWebSdkLicense: boolean;
	hasSiteScannerLicense: boolean;
	hasWfaLicense: boolean;
};

const defaultProps = {
	type: DropdownOptionType.LINK,
	hasBottomBorder: false,
	openInNewTab: true
};

export const getEvincedAppsDropdownMenuOptions = (
	licenses: getEvincedAppsMenuOptionsHelperArgs
): IDropdownMenuOption[] => {
	const { hasMfaLicense, hasMsdkLicense, hasWebSdkLicense, hasSiteScannerLicense, hasWfaLicense } =
		licenses;
	const isMFADashboard = window.location.href.includes(RoutesHelper.getMfaDashboardPath());
	const isWFADashboard = window.location.href.includes(RoutesHelper.getWfaDashboardPath());
	const isMobileSDK = window.location.href.includes(RoutesHelper.getMobileSdkDashboardPath());
	const isWebSDK = window.location.href.includes(RoutesHelper.getWebSdkDashboardPath());
	const isSiteScanner = window.location.href.includes(
		import.meta.env.VITE_SITE_SCANNER_DASHBOARD_URL
	);
	const isProductHub = window.location.href.includes(import.meta.env.VITE_PRODUCT_HUB_URL);

	const appsList = [
		{
			id: 'mfa-dashboard-url',
			url: RoutesHelper.getMfaDashboardPath(),
			title: 'Mobile Flow Analyzer',
			active: isMFADashboard,
			hasLicense: hasMfaLicense,
			...defaultProps
		},
		{
			id: 'mobile-sdk-dashboard-url',
			url: RoutesHelper.getMobileSdkDashboardPath(),
			title: 'Mobile SDKs',
			active: isMobileSDK,
			hasLicense: hasMsdkLicense,
			...defaultProps
		},
		{
			id: 'web-sdk-dashboard-url',
			url: RoutesHelper.getWebSdkDashboardPath(),
			title: 'Web SDKs',
			active: isWebSDK,
			hasLicense: hasWebSdkLicense,
			...defaultProps
		},
		{
			id: 'wfa-dashboard-url',
			url: RoutesHelper.getWfaDashboardPath(),
			title: 'Web Flow Analyzer',
			active: isWFADashboard,
			hasLicense: hasWfaLicense,
			...defaultProps
		},
		{
			id: 'site-scanner-url',
			url: import.meta.env.VITE_SITE_SCANNER_URL,
			title: 'Site Scanner',
			active: isSiteScanner,
			hasLicense: hasSiteScannerLicense,
			...defaultProps
		},
		{
			id: 'product-hub',
			url: import.meta.env.VITE_PRODUCT_HUB_URL,
			title: 'Product Hub',
			active: isProductHub,
			hasLicense: true,
			...defaultProps
		}
	];

	return appsList.filter((app) => app.hasLicense);
};

const rearrangeDropdownOptions = (
	options: IDropdownMenuOption[],
	desiredOrder: string[]
): IDropdownMenuOption[] => {
	const orderMap = new Map(desiredOrder.map((id, index) => [id, index]));

	// Grab and sort the matched ones by their order index
	const matched = options
		.filter((option) => orderMap.has(option.id))
		.sort((a, b) => orderMap.get(a.id) - orderMap.get(b.id));

	// The leftovers, in their original relative order
	const unmatched = options.filter((option) => !orderMap.has(option.id));

	return [...matched, ...unmatched];
};

const newDropdownMenuOrder = [
	'wfa-dashboard-url',
	'web-sdk-dashboard-url',
	'site-scanner-url',
	'mfa-dashboard-url',
	'mobile-sdk-dashboard-url',
	'product-hub'
];

// Used as a temporary solution until we have the new component
export const rearrangeAppMenuOptions = (
	list: IDropdownMenuOption[],
	isMobilePlatformDashboardEnabled = false
): IDropdownMenuOption[] => {
	const rearranged = rearrangeDropdownOptions(list, newDropdownMenuOrder);

	const options: IDropdownMenuOption[] = [
		{
			id: 'new-dashboard',
			url: RoutesHelper.getAccessibilityDashboardPath(),
			title: DASHBOARD_LABELS.WEB_DASHBOARD_TITLE,
			...defaultProps
		}
	];

	if (isMobilePlatformDashboardEnabled) {
		options.push({
			id: 'mobile-dashboard',
			url: RoutesHelper.getMobileAccessibilityDashboardPath(),
			title: DASHBOARD_LABELS.MOBILE_DASHBOARD_TITLE,
			...defaultProps
		});
	}

	options.push(...rearranged, {
		id: 'what-is-new',
		url: `${import.meta.env.VITE_PRODUCT_HUB_URL}/release-notes`,
		// eslint-disable-next-line prettier/prettier
		title: "What's New",
		...defaultProps
	});

	return options;
};
